import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import { useItemOrPriceResultLink } from '@/hooks/useItemOrPriceResultLink';
import { useAppSelector } from '@/redux/hooks';
import { getCatalogCoverLotsTransformed } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getItemSummaryCatalogId, getItemSummaryPromoted } from '@/redux/modules/item/summary/itemSummary.selectors';
import { mergedCaterwaulAndHammerUIThemes } from '@/theme/mergedCaterwaulAndHammerUIThemes';
import { ItemCardSource, useTrackItemCardClicked } from '@/utils/amplitude/analytics';

type Props = {
    catalogId: number;
    catalogPage?: boolean;
    catalogUrl?: string;
    className?: string;
};

export const CoverLotsWideComponent = (props: Props) => {
    const {
        breakpoints: { tabletNarrowWidthNum },
    } = mergedCaterwaulAndHammerUIThemes;
    const trackItemCardClicked = useTrackItemCardClicked();

    const mobile = !useIsWiderThan(tabletNarrowWidthNum);

    // the height is set at 300px for desktop and 125px for mobile
    const imageHeight = mobile ? 125 : 300;

    const { catalogId, className = '' } = props;
    const {
        primaryImage,
        primaryImageHighDpi,
        primaryItemId,
        primaryItemTitle,
        secondaryImage,
        secondaryImageHighDpi,
        secondaryItemId,
        secondaryItemTitle,
        tertiaryImage,
        tertiaryImageHighDpi,
        tertiaryItemId,
        tertiaryItemTitle,
    } = useAppSelector((state) => getCatalogCoverLotsTransformed(state, catalogId, imageHeight));

    const primaryItemUrl = useItemOrPriceResultLink(primaryItemId, true);
    const secondaryItemUrl = useItemOrPriceResultLink(secondaryItemId, true);
    const tertiaryItemUrl = useItemOrPriceResultLink(tertiaryItemId, true);

    const primaryCatalogId = useAppSelector((state) => getItemSummaryCatalogId(state, primaryItemId));
    const primaryPromoted = useAppSelector((state) => getItemSummaryPromoted(state, primaryItemId));
    const secondaryCatalogId = useAppSelector((state) => getItemSummaryCatalogId(state, secondaryItemId));
    const secondaryPromoted = useAppSelector((state) => getItemSummaryPromoted(state, secondaryItemId));
    const tertiaryCatalogId = useAppSelector((state) => getItemSummaryCatalogId(state, tertiaryItemId));
    const tertiaryPromoted = useAppSelector((state) => getItemSummaryPromoted(state, tertiaryItemId));

    const styledLinkWrapperClassname =
        'relative flex h-full basis-[calc(100%/3)] items-center justify-center overflow-hidden p-md smMax:p-2xs [&:nth-child(2)]:py-md [&:nth-child(2)]:smMax:py-2xs';
    const styledImageClassname = 'z-[10] max-h-full max-w-full object-contain';

    return (
        <div className={cn('relative flex h-[300px] max-h-[300px] w-full smMax:max-h-[125px]', className)}>
            {primaryImage && (
                <div className={styledLinkWrapperClassname}>
                    <RouteLink
                        className="flex h-full w-full items-center justify-center"
                        onClick={() => {
                            trackItemCardClicked({
                                catalogId: primaryCatalogId,
                                itemId: primaryItemId,
                                promoted: primaryPromoted,
                                source: ItemCardSource.CoverLots,
                            });
                        }}
                        to={primaryItemUrl}
                    >
                        <img
                            alt={primaryItemTitle}
                            className={styledImageClassname}
                            height={imageHeight}
                            src={primaryImage}
                            srcSet={`${primaryImageHighDpi} 2x`}
                            width={imageHeight}
                        />
                    </RouteLink>
                </div>
            )}
            {secondaryImage && (
                <div className={styledLinkWrapperClassname}>
                    <RouteLink
                        className="flex h-full w-full items-center justify-center"
                        onClick={() => {
                            trackItemCardClicked({
                                catalogId: secondaryCatalogId,
                                itemId: secondaryItemId,
                                promoted: secondaryPromoted,
                                source: ItemCardSource.CoverLots,
                            });
                        }}
                        to={secondaryItemUrl}
                    >
                        <img
                            alt={secondaryItemTitle}
                            className={styledImageClassname}
                            height={imageHeight}
                            src={secondaryImage}
                            srcSet={`${secondaryImageHighDpi} 2x`}
                            width={imageHeight}
                        />
                    </RouteLink>
                </div>
            )}
            {tertiaryImage && (
                <div className={styledLinkWrapperClassname}>
                    <RouteLink
                        className="flex h-full w-full items-center justify-center"
                        onClick={() => {
                            trackItemCardClicked({
                                catalogId: tertiaryCatalogId,
                                itemId: tertiaryItemId,
                                promoted: tertiaryPromoted,
                                source: ItemCardSource.CoverLots,
                            });
                        }}
                        to={tertiaryItemUrl}
                    >
                        <img
                            alt={tertiaryItemTitle}
                            className={styledImageClassname}
                            height={imageHeight}
                            src={tertiaryImage}
                            srcSet={`${tertiaryImageHighDpi} 2x`}
                            width={imageHeight}
                        />
                    </RouteLink>
                </div>
            )}
        </div>
    );
};
